import { fingerprintValue } from './quote-snapshot-diff';

export const WMS_ASSISTANT_MUTATION_TYPES = new Set([
    'wmsEditSection',
    'wmsAddActivity',
    'wmsRemoveActivity',
    'wmsReorderActivities',
    'wmsApplyTemplate',
    'wmsRegenerateFromCurrentScope',
    'wmsRecomputeActivityCost',
]);

const WMS_GUARD_SCHEMA_VERSION = 1;

// Persistence may replace browser-local photo bytes/locators with canonical
// blob, file-metadata or S3 receipts while preserving the exact builder-owned
// photo selection. Those storage details are not Work Method regeneration
// authority. Keep every semantic field (including captions, assignment stamps,
// markup and manual-edit metadata) while excluding only persistence transport.
const REGENERATION_PHOTO_STORAGE_KEYS = new Set([
    'data',
    'dataUrl',
    'originalData',
    'aiMarkupData',
    'blobId',
    's3Key',
    'fileMetadataId',
    'url',
    'thumbnailUrl',
    'uploadedAt',
    'cloudMedia',
    'contentType',
    'width',
    'height',
    'sizeKB',
    '_localBlobOnly',
    '_localMemoryOnly',
    '_cloudUploadPending',
    '_cloudUploadPendingReason',
    '_cloudUploadBlocked',
    '_cloudUploadBlockedReason',
    '_forcePhotoUpload',
    '_pendingValidation',
    '_pendingValidationAt',
]);

function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function structuredWms(projectWMS) {
    return isRecord(projectWMS?.structured) ? projectWMS.structured : null;
}

function activitiesFrom(projectWMS) {
    const structured = structuredWms(projectWMS);
    return structured && Array.isArray(structured.activities) ? structured.activities : [];
}

function regenerationPhotoFingerprintValue(photo) {
    if (!isRecord(photo)) return photo;
    const stableIdentity = [photo.id, photo.originalName, photo.name, photo.fileName]
        .map(value => String(value || '').trim())
        .find(Boolean);
    // Without a stable logical identity there is no safe way to prove that an
    // inline image and a later S3/file receipt describe the same photo. Keep
    // the complete object so anonymous replacements fail closed.
    if (!stableIdentity) return photo;
    return Object.keys(photo).reduce((semanticPhoto, key) => {
        if (!REGENERATION_PHOTO_STORAGE_KEYS.has(key)) {
            semanticPhoto[key] = photo[key];
        }
        return semanticPhoto;
    }, {});
}

function regenerationScopeFingerprintValue(scope) {
    if (!Array.isArray(scope)) return [];
    return scope.map((item) => {
        if (!isRecord(item) || !Array.isArray(item.images)) return item;
        return {
            ...item,
            images: item.images.map(regenerationPhotoFingerprintValue),
        };
    });
}

function regenerationQuoteFingerprintValue(quote) {
    if (!isRecord(quote)) return quote;
    const nextQuote = {
        ...quote,
        ...(Array.isArray(quote.scope)
            ? { scope: regenerationScopeFingerprintValue(quote.scope) }
            : {}),
    };
    if (isRecord(quote.header) && Object.prototype.hasOwnProperty.call(quote.header, 'heroImage')) {
        nextQuote.header = {
            ...quote.header,
            heroImage: regenerationPhotoFingerprintValue(quote.header.heroImage),
        };
    }
    return nextQuote;
}

function activityIdentity(activity, index) {
    const id = String(activity?.id || '').trim();
    return {
        key: id ? `id:${id}` : `content:${fingerprintValue(activity ?? null)}`,
        fingerprint: fingerprintValue(activity ?? null),
        proposedIndex: index,
    };
}

function sameActivityIdentity(left, right) {
    return !!left && !!right
        && left.key === right.key
        && left.fingerprint === right.fingerprint;
}

/**
 * Fingerprint the exact mutable Work Method state a proposal was reviewed
 * against. Generation metadata is included so an identical-looking pipeline
 * replacement still requires fresh review.
 */
export function fingerprintWmsAssistantBaseline(projectWMS) {
    const structured = structuredWms(projectWMS);
    if (!structured) return null;
    return fingerprintValue({
        version: projectWMS?.version ?? null,
        generatedAt: projectWMS?.generated_at ?? null,
        generatedBy: projectWMS?.generated_by ?? null,
        scopeHash: projectWMS?.scope_hash ?? projectWMS?.scopeHash ?? null,
        strategyHash: projectWMS?.strategy_hash ?? projectWMS?.strategyHash ?? null,
        structured,
    });
}

/**
 * Fingerprint the exact Stage 4 scope or Stage 5 quote that a full Work
 * Method regeneration proposal previewed. The source kind is part of the
 * receipt so an identical row array cannot silently cross stage authority.
 */
export function fingerprintWmsAssistantRegenerationSource(source) {
    if (!isRecord(source)) return null;
    if (source.kind === 'stage-5-quote' && isRecord(source.quote)) {
        return fingerprintValue({
            kind: source.kind,
            quote: regenerationQuoteFingerprintValue(source.quote),
            backingScope: regenerationScopeFingerprintValue(source.backingScope),
        });
    }
    if (source.kind === 'stage-4-scope' && Array.isArray(source.scope)) {
        return fingerprintValue({
            kind: source.kind,
            scope: regenerationScopeFingerprintValue(source.scope),
        });
    }
    return null;
}

/**
 * Create the persistent freshness receipt stored beside every pending WMS
 * mutation. target describes what the action will touch in the reviewed WMS.
 */
export function buildWmsAssistantProposalGuard(projectWMS, target = { kind: 'root' }) {
    const structured = structuredWms(projectWMS);
    const baselineFingerprint = fingerprintWmsAssistantBaseline(projectWMS);
    if (!structured || !baselineFingerprint) return null;
    const activities = activitiesFrom(projectWMS);
    const identities = activities.map(activityIdentity);
    let guardedTarget;

    switch (target?.kind) {
        case 'activity': {
            const index = Number(target.index);
            guardedTarget = {
                kind: 'activity',
                proposedIndex: index,
                identity: Number.isInteger(index) ? identities[index] || null : null,
                activityField: target.activityField || null,
                sectionKey: target.sectionKey || null,
            };
            break;
        }
        case 'activity-order':
            guardedTarget = { kind: 'activity-order', identities };
            break;
        case 'insertion': {
            const position = Math.max(0, Math.min(activities.length, Number(target.position) || 0));
            guardedTarget = {
                kind: 'insertion',
                position,
                leftIdentity: position > 0 ? identities[position - 1] : null,
                rightIdentity: position < identities.length ? identities[position] : null,
            };
            break;
        }
        case 'section': {
            const applyTarget = target.applyTarget || {};
            if (applyTarget.kind === 'activity') {
                const index = Number(applyTarget.activityIndex);
                guardedTarget = {
                    kind: 'activity-section',
                    sectionKey: target.sectionKey || null,
                    proposedIndex: index,
                    identity: Number.isInteger(index) ? identities[index] || null : null,
                    activityField: applyTarget.activityField || null,
                };
            } else {
                const topField = String(applyTarget.topField || '');
                guardedTarget = {
                    kind: 'top-section',
                    sectionKey: target.sectionKey || null,
                    topField,
                    sectionFingerprint: fingerprintValue(topField ? structured[topField] ?? null : null),
                };
            }
            break;
        }
        case 'root':
        default:
            guardedTarget = { kind: 'root', baselineFingerprint };
            if (target?.regenerationSource) {
                const sourceFingerprint = fingerprintWmsAssistantRegenerationSource(target.regenerationSource);
                if (sourceFingerprint) {
                    guardedTarget.regenerationSource = {
                        kind: target.regenerationSource.kind,
                        fingerprint: sourceFingerprint,
                        itemCount: target.regenerationSource.kind === 'stage-5-quote'
                            ? target.regenerationSource.quote?.scope?.length || 0
                            : target.regenerationSource.scope?.length || 0,
                    };
                }
            }
            break;
    }

    return {
        schemaVersion: WMS_GUARD_SCHEMA_VERSION,
        baselineFingerprint,
        activityCount: activities.length,
        target: guardedTarget,
    };
}

export function isWmsAssistantMutationAction(action) {
    const type = String(action?.type || '');
    return WMS_ASSISTANT_MUTATION_TYPES.has(type) || type.startsWith('wms');
}

function stale(message) {
    return {
        allowed: false,
        message: `${message} This Work Method proposal is stale and must be reviewed again. No Work Method changes were applied. Ask QM Assistant to prepare a new proposal.`,
    };
}

function validateTarget(action, guard, currentWMS, options = {}) {
    const target = guard?.target;
    const activities = activitiesFrom(currentWMS);
    const identities = activities.map(activityIdentity);
    if (!target || typeof target.kind !== 'string') {
        return stale('The proposal does not contain a stable Work Method target.');
    }

    if (target.kind === 'activity' || target.kind === 'activity-section') {
        const actionIndex = target.kind === 'activity-section'
            ? Number(action?.applyTarget?.activityIndex)
            : Number(action?.index);
        if (!Number.isInteger(actionIndex)
            || actionIndex !== target.proposedIndex
            || !sameActivityIdentity(identities[actionIndex], target.identity)) {
            return stale('The proposed Work Method activity target no longer matches the reviewed activity.');
        }
        if (target.kind === 'activity-section') {
            if (action?.applyTarget?.kind !== 'activity'
                || String(action?.applyTarget?.activityField || '') !== String(target.activityField || '')) {
                return stale('The proposed Work Method section target no longer matches the reviewed section.');
            }
        }
    } else if (target.kind === 'activity-order') {
        if (!Array.isArray(target.identities)
            || target.identities.length !== identities.length
            || target.identities.some((identity, index) => !sameActivityIdentity(identity, identities[index]))) {
            return stale('The Work Method activity order no longer matches the reviewed sequence.');
        }
        const order = action?.orderArray;
        const validOrder = Array.isArray(order)
            && order.length === identities.length
            && new Set(order).size === identities.length
            && order.every(index => Number.isInteger(index) && index >= 0 && index < identities.length);
        if (!validOrder) return stale('The proposed Work Method reorder target is no longer valid.');
    } else if (target.kind === 'insertion') {
        if (Number(action?.afterIndex) !== target.position
            || (target.leftIdentity && !sameActivityIdentity(identities[target.position - 1], target.leftIdentity))
            || (target.rightIdentity && !sameActivityIdentity(identities[target.position], target.rightIdentity))) {
            return stale('The proposed Work Method insertion point no longer matches the reviewed sequence.');
        }
    } else if (target.kind === 'top-section') {
        if (action?.applyTarget?.kind !== 'top'
            || String(action?.applyTarget?.topField || '') !== target.topField
            || fingerprintValue(currentWMS.structured[target.topField] ?? null) !== target.sectionFingerprint) {
            return stale('The proposed Work Method section target no longer matches the reviewed section.');
        }
    } else if (target.kind !== 'root') {
        return stale('The proposal contains an unsupported Work Method target.');
    }

    if (action?.type === 'wmsRegenerateFromCurrentScope') {
        const reviewedSource = target.regenerationSource;
        const getCurrentSource = options.getRegenerationSource;
        if (!reviewedSource?.fingerprint || typeof getCurrentSource !== 'function') {
            return stale('The full Work Method regeneration proposal has no reviewed Stage 4 or Stage 5 source proof.');
        }
        const currentSource = getCurrentSource(action);
        if (currentSource?.kind !== reviewedSource.kind
            || fingerprintWmsAssistantRegenerationSource(currentSource) !== reviewedSource.fingerprint) {
            return stale('The Stage 4 scope or Stage 5 quote changed after this regeneration proposal was reviewed.');
        }
    }

    return { allowed: true, message: null };
}

/** Validate the complete selected WMS mutation set before any setter or bridge runs. */
export function validateWmsAssistantMutationBatch(actions = [], currentWMS = null, options = {}) {
    const mutations = (Array.isArray(actions) ? actions : []).filter(isWmsAssistantMutationAction);
    if (mutations.length === 0) return { allowed: true, message: null };
    if (!structuredWms(currentWMS)) {
        return stale('The current structured Work Method is not available.');
    }
    const currentFingerprint = fingerprintWmsAssistantBaseline(currentWMS);
    for (const action of mutations) {
        const guard = action?.wmsGuard;
        if (!guard || guard.schemaVersion !== WMS_GUARD_SCHEMA_VERSION || !guard.baselineFingerprint) {
            return stale('This older pending proposal has no Work Method freshness proof.');
        }
        if (guard.baselineFingerprint !== currentFingerprint) {
            return stale('The Work Method changed since this proposal was reviewed.');
        }
        const targetResult = validateTarget(action, guard, currentWMS, options);
        if (!targetResult.allowed) return targetResult;
    }
    return { allowed: true, message: null };
}

function findEntry(entries, identity) {
    return entries.find(entry => sameActivityIdentity(entry.originalIdentity, identity)) || null;
}

function failAtomic(currentWMS, message) {
    return {
        allowed: false,
        message,
        nextWms: currentWMS,
        actionsExecuted: 0,
        appliedActions: [],
    };
}

function effectiveAssistantSectionText(currentSection, reviewedText) {
    if (isRecord(currentSection) && typeof currentSection._assistantEditedText === 'string') {
        return currentSection._assistantEditedText;
    }
    return typeof reviewedText === 'string' ? reviewedText : null;
}

function fingerprintCostAuthority(costBreakdown) {
    const authority = isRecord(costBreakdown) ? { ...costBreakdown } : {};
    // Timestamps describe when a recompute ran, not a pricing or authority
    // change. A proposal that only refreshes one must remain a true no-op.
    delete authority.lastUpdated;
    delete authority.last_updated;
    return fingerprintValue(authority);
}

/**
 * Apply synchronous WMS proposal actions to an isolated draft. The caller sets
 * state once only after this function returns a fully verified atomic result.
 * Full WMS regeneration stays on its existing async bridge and is only
 * freshness-validated here.
 */
export function applyWmsAssistantMutationBatch(currentWMS, actions = [], options = {}) {
    const mutations = (Array.isArray(actions) ? actions : []).filter(isWmsAssistantMutationAction);
    const validation = validateWmsAssistantMutationBatch(mutations, currentWMS, options);
    if (!validation.allowed) return failAtomic(currentWMS, validation.message);
    if (mutations.length === 0) {
        return { allowed: true, message: null, nextWms: currentWMS, actionsExecuted: 0, appliedActions: [] };
    }

    const synchronous = mutations.filter(action => action.type !== 'wmsRegenerateFromCurrentScope');
    if (synchronous.length === 0) {
        return { allowed: true, message: null, nextWms: currentWMS, actionsExecuted: 0, appliedActions: [] };
    }
    if (mutations.some(action => action.type === 'wmsRegenerateFromCurrentScope')) {
        return failAtomic(currentWMS, 'Work Method regeneration must be applied without other Work Method mutations. No Work Method changes were applied.');
    }
    if (synchronous.some(action => action.type === 'wmsApplyTemplate') && synchronous.length > 1) {
        return failAtomic(currentWMS, 'A Work Method template replacement must be applied on its own. No Work Method changes were applied.');
    }

    const originalActivities = activitiesFrom(currentWMS);
    let entries = originalActivities.map((value, index) => ({
        value,
        originalIdentity: activityIdentity(value, index),
    }));
    let structured = { ...currentWMS.structured };
    let actionsExecuted = 0;
    const appliedActions = [];
    const nowIso = typeof options.now === 'function' ? options.now() : new Date().toISOString();
    const createActivityId = typeof options.createActivityId === 'function'
        ? options.createActivityId
        : () => `asst_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

    for (const action of synchronous) {
        const target = action.wmsGuard.target;
        let changed = false;
        switch (action.type) {
            case 'wmsEditSection': {
                if (typeof action.newText !== 'string' || !action.applyTarget) {
                    return failAtomic(currentWMS, 'The Work Method section proposal is incomplete. No Work Method changes were applied.');
                }
                if (target.kind === 'top-section') {
                    const current = structured[target.topField];
                    if (effectiveAssistantSectionText(current, action.oldText) !== action.newText) {
                        const next = isRecord(current)
                            ? { ...current, _assistantEditedText: action.newText, _assistantEditedAt: nowIso }
                            : { _assistantEditedText: action.newText, _assistantEditedAt: nowIso };
                        structured[target.topField] = next;
                        changed = true;
                    }
                } else {
                    const entry = findEntry(entries, target.identity);
                    if (!entry) return failAtomic(currentWMS, 'The reviewed Work Method activity is no longer present. No Work Method changes were applied.');
                    if (effectiveAssistantSectionText(entry.value, action.oldText) !== action.newText) {
                        const next = {
                            ...entry.value,
                            _assistantEditedText: action.newText,
                            _assistantEditedField: target.activityField,
                            _assistantEditedAt: nowIso,
                        };
                        entry.value = next;
                        changed = true;
                    }
                }
                break;
            }
            case 'wmsAddActivity': {
                if (!String(action.name || '').trim()) {
                    return failAtomic(currentWMS, 'The Work Method activity proposal has no name. No Work Method changes were applied.');
                }
                const newEntry = {
                    originalIdentity: null,
                    value: {
                        id: createActivityId(),
                        title: action.name,
                        description: action.description || '',
                        why_this_method: '',
                        is_recommendation: false,
                        notes_to_client: '',
                        sequence_of_works: [],
                        trades_required: [],
                        materials_required: [],
                        equipment_required: [],
                        access_strategy: '',
                        programme_days: 0,
                        standards_applied: [],
                        risks_to_method: [],
                        quality_checkpoints: [],
                        notes_to_contractor: '',
                        costBreakdown: null,
                        _needs_pricing: true,
                        costBreakdownLocked: false,
                        applied_learnings_cited: [],
                        risk_refs: [],
                    },
                };
                const left = target.leftIdentity ? findEntry(entries, target.leftIdentity) : null;
                const right = target.rightIdentity ? findEntry(entries, target.rightIdentity) : null;
                let position = 0;
                if (left && right) {
                    const leftIndex = entries.indexOf(left);
                    const rightIndex = entries.indexOf(right);
                    if (rightIndex !== leftIndex + 1) return failAtomic(currentWMS, 'The reviewed Work Method insertion point changed during this batch. No Work Method changes were applied.');
                    position = rightIndex;
                } else if (left) position = entries.indexOf(left) + 1;
                else if (right) position = entries.indexOf(right);
                else if (entries.length > 0) return failAtomic(currentWMS, 'The reviewed Work Method insertion point is no longer present. No Work Method changes were applied.');
                entries.splice(position, 0, newEntry);
                changed = true;
                break;
            }
            case 'wmsRemoveActivity': {
                const entry = findEntry(entries, target.identity);
                if (!entry) return failAtomic(currentWMS, 'The reviewed Work Method activity is no longer present. No Work Method changes were applied.');
                entries = entries.filter(candidate => candidate !== entry);
                changed = true;
                break;
            }
            case 'wmsReorderActivities': {
                if (entries.some(entry => !entry.originalIdentity)) {
                    return failAtomic(currentWMS, 'The Work Method activity set changed during this batch. No Work Method changes were applied.');
                }
                const reordered = action.orderArray.map(index => findEntry(entries, target.identities[index]));
                if (reordered.some(entry => !entry) || new Set(reordered).size !== entries.length) {
                    return failAtomic(currentWMS, 'The reviewed Work Method reorder can no longer be resolved. No Work Method changes were applied.');
                }
                changed = reordered.some((entry, index) => entry !== entries[index]);
                if (changed) entries = reordered;
                break;
            }
            case 'wmsApplyTemplate': {
                if (!Array.isArray(action.activities)) {
                    return failAtomic(currentWMS, 'The Work Method template proposal is incomplete. No Work Method changes were applied.');
                }
                if (fingerprintValue(entries.map(entry => entry.value)) !== fingerprintValue(action.activities)) {
                    entries = action.activities.map(value => ({ value, originalIdentity: null }));
                    changed = true;
                }
                break;
            }
            case 'wmsRecomputeActivityCost': {
                if (!isRecord(action.recomputed)) {
                    return failAtomic(currentWMS, 'The Work Method cost proposal is incomplete. No Work Method changes were applied.');
                }
                const entry = findEntry(entries, target.identity);
                if (!entry) return failAtomic(currentWMS, 'The reviewed Work Method activity is no longer present. No Work Method changes were applied.');
                const nextCostBreakdown = { ...(entry.value?.costBreakdown || {}), ...action.recomputed };
                if (fingerprintCostAuthority(entry.value?.costBreakdown) !== fingerprintCostAuthority(nextCostBreakdown)) {
                    entry.value = { ...entry.value, costBreakdown: nextCostBreakdown };
                    changed = true;
                }
                break;
            }
            default:
                return failAtomic(currentWMS, `Unsupported Work Method mutation "${action.type}". No Work Method changes were applied.`);
        }
        if (changed) {
            actionsExecuted += 1;
            appliedActions.push(action);
        }
    }

    if (actionsExecuted === 0) {
        return { allowed: true, message: null, nextWms: currentWMS, actionsExecuted: 0, appliedActions: [] };
    }
    structured = { ...structured, activities: entries.map(entry => entry.value) };
    return {
        allowed: true,
        message: null,
        nextWms: {
            ...currentWMS,
            structured,
            generated_by: 'human-edited',
            fallback: false,
        },
        actionsExecuted,
        appliedActions,
    };
}
